import React, { useMemo } from 'react';
import { Briefcase, FileText, Calendar } from 'lucide-react';
import { formatearFechaCorta, formatearPrecio } from '../../../../../utils/utils';
import { useContabilidadModel } from '../../../../../modelos/Contabilidad/modeloContabilidad';
import { generarPlanDePagos, generarYVerificarPlanCompleto } from '../Components/FuncionesProPlataforma';
import EditableField from '../../../../../components/EditableFields';

const ServiciosProPlataforma = ({serviciosFiltrados,nombreEmpresaReal,solicitudesFiltradas}) => {

  const { updateServicio } = useContabilidadModel();

  // Calcula los totales generales de TODOS los servicios filtrados

  //pagos de clientes realizados
  const totalPagosCliente = serviciosFiltrados
    .flatMap(servicio => servicio.pagos || [])
    .filter(pago => pago.tipopago === 'CLIENTES')
    .reduce((total, pago) => total + pago.valor, 0);

  const historialPagosAgrupados = useMemo(() => {
          // 1. Agrupa todos los pagos válidos por su fecha (en formato string)
          const pagosPorFecha = serviciosFiltrados
              .flatMap(servicio => servicio.pagos || [])
              .filter(pago => pago.tipopago === 'CLIENTES')
              .reduce((acc, pago) => {
                  const fechaPago = new Date(pago.fechapago);
                  // Salta cualquier pago que tenga una fecha inválida para evitar errores
                  if (isNaN(fechaPago.getTime())) { 
                      console.warn('Fecha de pago inválida encontrada, saltando pago:', pago);
                      return acc;
                  }
                  const fechaKey = fechaPago.toLocaleDateString('es-ES');
                  if (!acc[fechaKey]) {
                      acc[fechaKey] = { fechaOriginal: fechaPago, pagos: [] };
                  }
                  acc[fechaKey].pagos.push(pago);
                  return acc;
              }, {});
  
          // 2. Itera sobre cada grupo de fecha para procesar sus pagos internos
          const resultadoFinal = Object.values(pagosPorFecha).map(grupoDia => {
              // Para cada día, agrupa los pagos por referencia y suma sus valores
              const pagosSumadosPorReferencia = grupoDia.pagos.reduce((acc, pago) => {
                  const referencia = pago.referencia || 'N/A';
                  if (!acc[referencia]) {
                      // Mantiene la estructura { referencia, valor } que la UI espera
                      acc[referencia] = { referencia: referencia, valor: 0 };
                  }
                  acc[referencia].valor += pago.valor;
                  return acc;
              }, {});
  
              // Calcula el total del día sumando los subtotales de cada referencia
              const totalDia = Object.values(pagosSumadosPorReferencia).reduce((sum, pago) => sum + pago.valor, 0);
  
              // Devuelve la estructura final que la UI espera: un objeto para el día con un array de pagos internos
              return {
                  fechaOriginal: grupoDia.fechaOriginal,
                  totalDia: totalDia,
                  pagos: Object.values(pagosSumadosPorReferencia) // `pagos` es ahora un array, lo que corrige el error .map()
              };
          });
  
          // 3. Ordena los grupos de fechas del más reciente al más antiguo
          return resultadoFinal.sort((a, b) => new Date(b.fechaOriginal) - new Date(a.fechaOriginal));
  }, [serviciosFiltrados]);

  const granTotalCobrado = serviciosFiltrados.reduce((total, s) => total + s.preciocobrado, 0);
  const granTotalTutor = serviciosFiltrados.reduce((total, s) => total + s.preciotutor, 0);
  const granTotalGanancia = granTotalCobrado - granTotalTutor;
  
  const percentPagos = totalPagosCliente/granTotalCobrado;
  const debeCliente = granTotalCobrado - totalPagosCliente

  //actualizar servicio
  const handleSave = (field,value,codigo) =>{
    console.log("a actualizar servicio",field,value,codigo);

    updateServicio(codigo,nombreEmpresaReal,field,value,'field');

  }

  return (
     <div className="space-y-6">
            {serviciosFiltrados.length > 0 ? (
                <>
                    {Object.entries(
                        // 1. Agrupar los servicios por materia
                        serviciosFiltrados.reduce((grupos, servicio) => {
                            const materia = servicio.proPlataforma.materia || 'Sin Materia Asignada';
                            if (!grupos[materia]) {
                                grupos[materia] = [];
                            }
                            grupos[materia].push(servicio);
                            return grupos;
                        }, {})
                    ).map(([materia, serviciosGrupo]) => {
                        // 2. Calcular totales para cada grupo de materia
                        const totalCobradoMateria = serviciosGrupo.reduce((total, s) => total + s.preciocobrado, 0);
                        const totalPagadoTutorMateria = serviciosGrupo.reduce((total, s) => total + s.preciotutor, 0);
                        const gananciaMateria = totalCobradoMateria - totalPagadoTutorMateria;

                        return (
                            <div key={materia} className="bg-white rounded-xl shadow-lg overflow-hidden">
                                {/* Header de la materia */}
                                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 border-b border-gray-200">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-3">
                                            <Briefcase className="h-6 w-6 text-purple-600" /> {/* Ícono de ejemplo */}
                                            <h3 className="text-xl font-bold text-gray-800">{materia}</h3>
                                        </div>
                                        <span className="bg-purple-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                                            {serviciosGrupo.length} servicio{serviciosGrupo.length !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                </div>

                                {/* Tabla de servicios para el grupo */}
                                <div className="p-6">
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="bg-gray-50">
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fase</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Precios</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Unad</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado de entregas</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado de Notas</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {serviciosGrupo
                                                    .sort((a, b) => new Date(a.proPlataforma.fechaRealUnad) - new Date(b.proPlataforma.fechaRealUnad))
                                                    .map((servicio, index) => (
                                                    <tr key={servicio.codigo || index} className="hover:bg-gray-50">
                                                        {/* Codigo servicio */}
                                                        <td className="px-4 py-4 text-sm font-medium text-purple-600">{servicio.codigo}</td>

                                                        {/* Fase */}
                                                        <td className="px-4 py-4 text-sm text-gray-900">{servicio.proPlataforma.fase}</td>

                                                        {/* Precios */}
                                                        <td className="px-4 py-4 text-sm whitespace-nowrap">
                                                        {/* El contenedor flex-col apila verticalmente cada div interior */}
                                                        <div className="flex flex-col space-y-1 items-start">
                                                            
                                                            {/* Fila para el Precio del Tutor */}
                                                            <div>
                                                            <span className="text-xs text-gray-500">Tutor: </span>
                                                            <span className="font-medium text-gray-800">
                                                                {formatearPrecio(servicio.preciotutor)}
                                                            </span>
                                                            </div>

                                                            {/* Fila para el Precio Cobrado */}
                                                            <div>
                                                            <span className="text-xs text-gray-500">Cobrado: </span>
                                                            <span className="font-medium text-green-700">
                                                                {formatearPrecio(servicio.preciocobrado)}
                                                            </span>
                                                            </div>

                                                        </div>
                                                        </td>

                                                        {/* Fecha UNAD */}
                                                        <td className="px-4 py-4 text-sm text-gray-900 font-semibold">{formatearFechaCorta(servicio.proPlataforma.fechaRealUnad)}</td>
                                                        
                                                        {/* Estado de pagos ,entregas y check admin */}
                                                        <td className="px-4 py-4 text-sm">
                                                            {/* Contenedor principal que apila todo verticalmente */}
                                                            <div className="flex flex-col space-y-3 items-start">

                                                                {/* 1. Estado de Pagos */}
                                                                <div>
                                                                <div className="text-xs font-semibold text-gray-500 mb-1">Estado de pagos:</div>
                                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                                    servicio.getPorcentajePagoCliente() >= 100 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                                                }`}>
                                                                    {servicio.getPorcentajePagoCliente()}%
                                                                </span>
                                                                </div>

                                                                {/* 2. Estado de Entregas */}
                                                                <div>
                                                                <div className="text-xs font-semibold text-gray-500 mb-1">Estado de entregas:</div>
                                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                                    servicio.getTrabajoEntregado() ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'
                                                                }`}>
                                                                    {servicio.getTrabajoEntregado() ? 'Entregado' : 'Pendiente'}
                                                                </span>
                                                                </div>

                                                                {/* 3. Check Admin */}
                                                                <div>
                                                                <div className="text-xs font-semibold text-gray-500 mb-1">Check admin:</div>
                                                                {servicio.servicioVerificado ? (
                                                                    // Si es TRUE, muestra el indicador de "Verificado"
                                                                    <div className="flex items-center justify-center text-green-600 font-semibold">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                                    </svg>
                                                                    Verificado
                                                                    </div>
                                                                ) : (
                                                                    // Si es FALSE, muestra el botón para verificar
                                                                    <button
                                                                    onClick={() => handleSave('servicioVerificado', true, servicio.codigo)}
                                                                    className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-1 px-3 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                                                    >
                                                                    Marcar como Verificado
                                                                    </button>
                                                                )}
                                                                </div>

                                                            </div>
                                                        </td>

                                                        {/* Estado de notas */}
                                                        <td className="px-4 py-4 text-sm text-gray-900 font-semibold">
                                                            <EditableField
                                                                field="notaObtenida"
                                                                label="Nota obtenida"
                                                                value={servicio.notaObtenida}
                                                                type="number"
                                                                isAdmin={true}
                                                                onSave={(field, value) => handleSave(field,value,servicio.codigo)}
                                                            />
                                                        </td>


                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    
                                    {/* Subtotales por materia */}
                                    <div className="mt-4 bg-purple-50 rounded-lg p-4 border-l-4 border-purple-500">
                                        <div className="flex justify-between items-center">
                                            <h4 className="font-semibold text-purple-800">Subtotal {materia}:</h4>
                                            <div className="flex space-x-6">
                                                <span className="text-purple-800 font-bold">Cobrado: {formatearPrecio(totalCobradoMateria)}</span>
                                                <span className="text-purple-800 font-bold">Tutor: {formatearPrecio(totalPagadoTutorMateria)}</span>
                                                <span className="text-purple-800 font-bold">Ganancia: {formatearPrecio(gananciaMateria)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl shadow-lg p-6 mt-8 border-l-4 border-green-500">
                          <h3 className="text-2xl font-bold text-green-800 mb-6">Totales Generales de Servicios</h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                              <div>
                                  <div className="text-4xl font-bold text-green-600">{formatearPrecio(granTotalCobrado)}</div>
                                  <div className="text-sm font-semibold text-green-800 mt-1">Total Cobrado </div>
                              </div>
                              <div>
                                  <div className="text-4xl font-bold text-orange-600">{formatearPrecio(granTotalTutor)}</div>
                                  <div className="text-sm font-semibold text-orange-800 mt-1">Total Costo Tutor</div>
                              </div>
                              <div>
                                  <div className="text-4xl font-bold text-emerald-600">{formatearPrecio(granTotalGanancia)}</div>
                                  <div className="text-sm font-semibold text-emerald-800 mt-1">Ganancia Total</div>
                              </div>
                              <div>
                                  <div className="text-4xl font-bold text-green-600">{formatearPrecio(totalPagosCliente)}</div>
                                  <div className="text-sm font-semibold text-green-800 mt-1">Total Pagado por cliente </div>
                              </div>
                              <div>
                                  <div className="text-4xl font-bold text-green-600">{formatearPrecio(debeCliente)}</div>
                                  <div className="text-sm font-semibold text-green-800 mt-1">Debe el cliente</div>
                              </div>
                              <div>
                                  <div className="text-4xl font-bold text-green-600">{percentPagos*100}</div>
                                  <div className="text-sm font-semibold text-green-800 mt-1">% cancelado</div>
                              </div>
                          </div>
                      </div>

                    {/* Sección para mostrar el historial de pagos del cliente */}
                    {historialPagosAgrupados.length > 0 && (
                          <div className="mt-8">
                              <h3 className="text-2xl font-bold text-gray-800 mb-4">Historial de Pagos del Cliente</h3>
                              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                                  <div className="overflow-x-auto">
                                      <table className="min-w-full">
                                          <thead className="bg-gray-50">
                                              <tr>
                                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha de Pago</th>
                                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Referencia</th>
                                                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Valor</th>
                                              </tr>
                                          </thead>
                                          <tbody className="divide-y divide-gray-200">
                                              {historialPagosAgrupados.map((pago, index) => (
                                                  <tr key={index} className="hover:bg-gray-50">
                                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                          <div className="flex items-center">
                                                              {/* Esto ya estaba correcto */}
                                                              <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                                                              {new Date(pago.fechaOriginal).toLocaleDateString('es-ES', {
                                                                  year: 'numeric', month: 'long', day: 'numeric'
                                                              })}
                                                          </div>
                                                      </td>
                                                      {/* CORRECCIÓN DE REFERENCIA */}
                                                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                          {pago.pagos.map(p => p.referencia).join(', ')}
                                                      </td>
                                                      {/* CORRECCIÓN DE VALOR */}
                                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-800">
                                                          {formatearPrecio(pago.totalDia)}
                                                      </td>
                                                  </tr>
                                              ))}
                                          </tbody>
                                      </table>
                                  </div>
                              </div>
                          </div>
                    )}


                </>
            ) : (
                // Mensaje cuando no hay servicios filtrados
                <div className="text-center py-12 bg-white rounded-xl shadow-lg">
                    <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-500 text-lg">No hay servicios que coincidan con los filtros seleccionados.</p>
                </div>
            )}
        </div>
  )

}

export default ServiciosProPlataforma
